import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Brand, PRODUCT_NAME } from '@/components/shared/brand';

const UPDATED = '15 October 2026';

function LegalLayout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b border-slate-200">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-4 sm:px-6">
          <Link to="/" aria-label={PRODUCT_NAME}>
            <Brand className="h-12" />
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" />
            Home
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <h1 className="text-3xl font-extrabold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-slate-500">Last updated {UPDATED}</p>
        <div className="mt-8 space-y-6 text-[0.9375rem] leading-relaxed text-slate-700 [&_h2]:mt-8 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-slate-900 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:ps-5">
          {children}
        </div>
      </main>
    </div>
  );
}

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service">
      <p>
        These terms govern your use of {PRODUCT_NAME} (“the Service”), a customer relationship management platform
        operated from the United Arab Emirates. By creating a workspace or using the Service you agree to them on
        behalf of yourself and the company you represent.
      </p>
      <h2>1. Your workspace</h2>
      <p>
        Each company has its own workspace. The person who creates it is its first administrator and is
        responsible for the users they invite, the permissions they grant and the data entered into it.
      </p>
      <h2>2. Free trial and subscriptions</h2>
      <p>
        New workspaces include a 14-day free trial. After the trial a paid plan is required to keep using the
        Service. Fees are quoted in UAE dirhams (AED) and exclude 5% VAT, which is added to every invoice. Plans
        renew automatically until cancelled.
      </p>
      <h2>3. Acceptable use</h2>
      <ul>
        <li>Do not send spam or messages to people who have not agreed to receive them.</li>
        <li>Follow WhatsApp, Meta and email-provider policies when messaging customers.</li>
        <li>Do not upload unlawful content or try to access another company's workspace.</li>
      </ul>
      <h2>4. Your data</h2>
      <p>
        You own the data in your workspace. We process it only to provide the Service, as described in our{' '}
        <Link to="/privacy" className="font-medium text-link hover:underline">
          Privacy Policy
        </Link>
        . You may export or delete it at any time.
      </p>
      <h2>5. Availability</h2>
      <p>
        We work to keep the Service available at all times but do not guarantee it will be uninterrupted. Planned
        maintenance is announced in advance where possible.
      </p>
      <h2>6. Liability</h2>
      <p>
        To the extent permitted by UAE law, our total liability for any claim is limited to the fees you paid in
        the twelve months before it arose.
      </p>
      <h2>7. Governing law</h2>
      <p>These terms are governed by the laws of the United Arab Emirates and the Emirate of Dubai.</p>
      <h2>8. Contact</h2>
      <p>
        Questions about these terms:{' '}
        <a href="mailto:hello@dovocrm.com" className="font-medium text-link hover:underline">
          hello@dovocrm.com
        </a>
      </p>
    </LegalLayout>
  );
}

export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        This policy explains how {PRODUCT_NAME} handles personal data, in line with the UAE Personal Data
        Protection Law (Federal Decree-Law No. 45 of 2021).
      </p>
      <h2>1. What we collect</h2>
      <ul>
        <li>Account details: name, work email, phone number and company name.</li>
        <li>Data you store in your workspace: contacts, deals, messages, files and notes.</li>
        <li>Technical data: IP address, browser and sign-in times, used for security.</li>
      </ul>
      <h2>2. How we use it</h2>
      <p>
        To provide and secure the Service, to bill your subscription, and to contact you about your account. We do
        not sell personal data or use your workspace data for advertising.
      </p>
      <h2>3. Controller and processor</h2>
      <p>
        For the data you put in your workspace, your company is the controller and we act as its processor. You
        are responsible for having a lawful basis to store and contact the people in it.
      </p>
      <h2>4. Security</h2>
      <p>
        Workspaces are isolated from each other. Passwords are hashed with Argon2, connections are encrypted,
        two-factor authentication is available and changes are recorded in an audit log.
      </p>
      <h2>5. Retention and deletion</h2>
      <p>
        Your data is kept while your workspace is active. When you close it, we delete it within 90 days unless the
        law requires us to keep it longer.
      </p>
      <h2>6. Your rights</h2>
      <p>
        You can ask to access, correct, export or delete your personal data at{' '}
        <a href="mailto:privacy@dovocrm.com" className="font-medium text-link hover:underline">
          privacy@dovocrm.com
        </a>
        .
      </p>
    </LegalLayout>
  );
}
